import {Chip, List, ListItemIcon, ListItemText, MenuItem, Popover, Stack, SxProps, Typography} from '@mui/material'
import React, {memo, useCallback, useEffect, useMemo, useState} from 'react'
import {strategies, StrategiesValue} from '../../util/strategyBuilder'
import {useSelector} from 'react-redux'
import {ExtraSmallButton} from '../custom'
import {selectOrderType, selectPredefinedStrategy} from '../../selectors/strategyBuilder'
import useOnStrategySelected from '../../hooks/useonStrategySelected'
import {selectStrategyTemplates} from '../../selectors/strategyTemplate'
import {  parseStrategyTemplateOrders, strategyParser } from '../strategyTemplate/StrategyParser'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import {selectUnderlyingOptionsTradeData} from '../../selectors/options'
import {StrategyTemplateProto} from '../../proto/generated/strategy'
import {CategoryGroupNameToCode, StrategyGroup} from '../strategyTemplate/StrategyTemplate.types'
import {OptionChainViewKey, STRATEGY_LOGOS_BASE_URL} from '../../assets/constants'
import { fetchStrategyTemplates } from '../../services/apis/strategyTemplate'
import { setPredefinedStrategy, setStrategyGroup, setViewTabs, updateTradeOrders } from '../../reducer/strategyBuilder.reducer'
import { useDispatch } from 'react-redux'
import AddIcon from '../../assets/icons/AddIcon'
import { setOpenStrategyTemplateDialog } from '../../reducer/strategyTemplates.reducer'
import { WidgetListItems } from '../../util/sidebar'
import commonActions from '../../actions/common.actions'

type Props = {
    giveList?: boolean
    listType?: StrategyGroup
    optionChainViewKey?: OptionChainViewKey
    onStrategyClicked?: (template: StrategyTemplateProto) => void
    showAddTemplate?: boolean
    disabled?:boolean
    sxProps?: SxProps
    error?: boolean
    errorMessage?: string
}

const StrategiesMenu: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch()
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()
    const { giveList = false, listType = StrategyGroup.NEUTRAL, optionChainViewKey = OptionChainViewKey.LIVE, showAddTemplate = false, disabled, error, errorMessage } = props
    const value = useSelector(selectPredefinedStrategy);
    const orderType = useSelector(selectOrderType)
    const [selectedStrategyGroup, setSelectedStrategyGroup] = useState<StrategyGroup>(StrategyGroup.BULLISH)
    const {loading, onStrategySelected: selectStrategy} = useOnStrategySelected()
    const strategyTemplates = useSelector(selectStrategyTemplates)
    const {primaryOrUnderlying: mainSecurity, primaryOrUnderlyingToken: underlyingToken} = useSelectPrimaryOrUnderlyingSecurity()
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(underlyingToken))

    useEffect(() => {
        if (strategyTemplates.length === 0) {
            fetchStrategyTemplates()
        }
    }, [])

    const handleClose = useCallback(() => {
        setAnchorEl(undefined)
    }, [])

    const onStrategySelected = useCallback((value: StrategiesValue) => {
        selectStrategy(value)
        handleClose()
    }, [handleClose, selectStrategy])

    const startIcon = useMemo(() => {
        return (value ? strategies.find((s) => s.label === value)?.icon : undefined)
    }, [value])

    const handleCustomStrategyTemplateClick = useCallback(async (template: StrategyTemplateProto) => {
        if(props.onStrategyClicked) {
            props.onStrategyClicked(template)
            if(!showAddTemplate) dispatch(setPredefinedStrategy(template.name))
            handleClose()
        } else {
            if (optionChainViewKey === OptionChainViewKey.LIVE) {
                const tradeOrders = await parseStrategyTemplateOrders(template, mainSecurity!, optionExpiries, orderType)
                commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
                dispatch(setStrategyGroup(`${mainSecurity!.displayName} - ${template.name}`))
                dispatch(updateTradeOrders(tradeOrders));
                dispatch(setPredefinedStrategy(template.name))
            } else {
                await strategyParser(template, mainSecurity! , optionExpiries, optionChainViewKey)
            }
        }
    }, [props, dispatch,handleClose, mainSecurity, optionExpiries, optionChainViewKey, showAddTemplate, orderType])

    if (giveList) {
        return (
            <List>
                {
                    strategyTemplates.filter((s) => CategoryGroupNameToCode[s.group as any] === listType).map(strategy => (
                        <MenuItem key={`${strategy.group} ${strategy.id}`}
                                  dense
                                  value={strategy.name}
                                  onClick={() => handleCustomStrategyTemplateClick(strategy)}
                                  data-event={'Trade Options Strategy Selected'}
                                  data-event-strategy={strategy.name}
                                  sx={{pl: 4}}
                        >
                            <ListItemIcon>
                                {strategy.imageUrl ? (
                                    <img
                                        src={`${STRATEGY_LOGOS_BASE_URL}${strategy.imageUrl}.svg`}
                                        alt='icon'
                                        style={{width: '20px', height: '20px'}}
                                    />
                                ) : null}
                            </ListItemIcon>
                            <ListItemText>{strategy.name}</ListItemText>
                        </MenuItem>
                    ))
                }
            </List>
        )
    }

    return (

        <>
            <ExtraSmallButton
                color={error ? "error" : "inherit"}
                id="select-strategy-button"
                variant="outlined"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-haspopup="true"
                disabled={disabled}
                startIcon={startIcon}
                sx={{
                    height: '32px',
                    borderColor: error ? theme => theme.palette.error.main : showAddTemplate ? 'inherit' : theme => theme.palette.background.default,
                    textWrap: 'nowrap',
                    '& .MuiButton-startIcon': {
                        marginLeft: '4px'
                    },
                    ...(props?.sxProps??{})
                }}
            >
                <Typography variant={"body2"} sx={{
                    minWidth: '100px',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>{value !== undefined ? value : showAddTemplate ? "Select" :  'Custom'}</Typography>
            </ExtraSmallButton>
            <Typography variant='caption' color='error'>{error ? errorMessage : ''}</Typography>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                sx={{
                    cursor: `${loading ? 'not-allowed' : 'pointer'}`,
                    zIndex: 1000000,
                }}
            >
                {
                    <Stack direction={'row'} spacing={1} sx={{py: '8px', mx: '16px'}}>
                        {Object.values(StrategyGroup).map((group) => (
                            <Chip key={group} onClick={(e) => {
                                setSelectedStrategyGroup(group)
                                e.stopPropagation()
                            }} label={group} sx={{opacity: group === selectedStrategyGroup ? 1 : 0.5}}
                                  data-event={'Options Strategy Group Changed'}
                                  data-event-strategy_group={group}
                            />
                        ))}
                        {
                            showAddTemplate ? <Chip variant='outlined' onClick={(e) => {
                                dispatch(setOpenStrategyTemplateDialog(true))
                                handleClose()
                                e.stopPropagation()
                            }} label={<span style={{ display: 'flex', alignItems: 'center' }}><AddIcon />Add template</span>}
                            /> : null
                        }
                    </Stack>
                }

                {strategyTemplates.filter((s) => CategoryGroupNameToCode[s.group as any] === selectedStrategyGroup).map(strategy => (
                    <MenuItem key={`${strategy.group} ${strategy.id}`}
                              value={strategy.name}
                              selected={value === strategy.name}
                              onClick={() => handleCustomStrategyTemplateClick(strategy)}
                              data-event={'Trade Options Strategy Selected'}
                              data-event-strategy={strategy.name}
                    >
                        <ListItemIcon>{strategy.imageUrl ? <img src={`${STRATEGY_LOGOS_BASE_URL}${strategy.imageUrl}.svg`} alt='icon'/> : null}</ListItemIcon>
                        <ListItemText sx={{paddingLeft: 1}} primary={strategy.name} secondary={strategy.description}/>
                    </MenuItem>
                ))}
            </Popover>
        </>
    )
}

export default memo(StrategiesMenu)